/**
 * Every price change for one customer × job type, newest first (gap 1, DEC-383). A change
 * that a later one on the same day replaced is marked; rows from an import say so. Who and
 * when render in the user's time zone (DEC-004).
 */

import React from 'react';
import { Badge } from 'src/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { formatTimestamp } from '../../lib/timestamp';
import { useUserTimezone } from '../../lib/use-user-timezone';
import { formatDay, priceText } from './customer-prices';
import { useJobTypePriceHistory } from './customer-prices-hooks';

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export interface JobTypePriceHistoryProps {
  readonly customerId: string;
  readonly familyId: string;
  readonly label: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function JobTypePriceHistory(props: JobTypePriceHistoryProps): React.JSX.Element {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent style={{ maxWidth: '760px' }}>
        <DialogHeader>
          <DialogTitle>{`Price history: ${props.label}`}</DialogTitle>
          <DialogDescription>Every change, newest first.</DialogDescription>
        </DialogHeader>
        {props.open && <HistoryRows customerId={props.customerId} familyId={props.familyId} />}
      </DialogContent>
    </Dialog>
  );
}

function HistoryRows(props: {
  readonly customerId: string;
  readonly familyId: string;
}): React.JSX.Element {
  const tz = useUserTimezone();
  const history = useJobTypePriceHistory(props.customerId, props.familyId);

  if (history.isLoading) {
    return (
      <p role="status" style={helpStyle}>
        Loading…
      </p>
    );
  }
  if (history.isError || history.data === undefined) {
    return (
      <p role="alert" style={{ color: 'var(--color-danger)' }}>
        Could not load the history. Try again.
      </p>
    );
  }
  if (history.data.rows.length === 0) {
    return <p style={helpStyle}>No changes yet.</p>;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Starts</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead>Entered</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.data.rows.map((h) => (
            <TableRow key={h.id}>
              <TableCell>{formatDay(h.effectiveFrom)}</TableCell>
              <TableCell>
                {priceText(h.rateCents, h.billingMethod)}
                {h.correctedSameDay && (
                  <Badge variant="secondary" style={{ marginLeft: '8px' }}>
                    corrected the same day
                  </Badge>
                )}
              </TableCell>
              <TableCell className="keep-case">
                {h.notes ?? '—'}
                {h.importId !== null && (
                  <Badge variant="outline" style={{ marginLeft: '8px' }}>
                    Imported
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                {h.createdByName ?? '—'}
                <div style={helpStyle}>{formatTimestamp(h.createdAt, tz)}</div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {history.data.truncated && <p style={helpStyle}>Showing the latest 200 changes.</p>}
    </>
  );
}
